import React from "react";

import DropdownField from "~/components/dropdownField";
import PrimaryButton from "~/components/buttons/primaryButton";
import SecondaryButton from "~/components/buttons/secondaryButton";
import { getApiErrorMessage } from "~/services/apiErrors";
import { RESOLVE_CALL_OPTIONS } from "~/utils/callResolution";

export type ResolveCallSubmission = {
  category: string;
  notes: string;
  followUpRequired: boolean;
  timestampMs: number;
};

type ResolveCallModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onDeferAndNavigateDashboard?: () => void;
  onSubmit: (payload: ResolveCallSubmission) => void | Promise<void>;
  submitError?: string | null;
  className?: string;
};

const REQUIRED_NOTES_CATEGORIES = new Set<string>(["Emergency", "Other"]);

function cn(...classes: Array<string | undefined | false | null>) {
  return classes.filter(Boolean).join(" ");
}

export default function ResolveCallModal({
  isOpen,
  onClose,
  onDeferAndNavigateDashboard,
  onSubmit,
  submitError: externalSubmitError,
  className,
}: ResolveCallModalProps) {
  const [category, setCategory] = React.useState<string | null>(null);
  const [notes, setNotes] = React.useState("");
  const [followUpRequired, setFollowUpRequired] = React.useState(false);
  const [categoryError, setCategoryError] = React.useState<string | null>(null);
  const [notesError, setNotesError] = React.useState<string | null>(null);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const notesRequired = category
    ? REQUIRED_NOTES_CATEGORIES.has(category)
    : false;

  React.useEffect(() => {
    if (!isOpen) return;
    setCategory(null);
    setNotes("");
    setFollowUpRequired(false);
    setCategoryError(null);
    setNotesError(null);
    setSubmitError(null);
    setSubmitting(false);
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const handleSubmit = React.useCallback(async () => {
    const nextNotes = notes.trim();
    let hasError = false;

    if (!category) {
      setCategoryError("Call category is required.");
      hasError = true;
    } else {
      setCategoryError(null);
    }

    if (category && REQUIRED_NOTES_CATEGORIES.has(category) && !nextNotes) {
      setNotesError("Resolution notes are required for this category.");
      hasError = true;
    } else {
      setNotesError(null);
    }

    if (hasError) {
      return;
    }
    if (!category) {
      return;
    }

    setSubmitError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        category,
        notes: nextNotes,
        followUpRequired,
        timestampMs: Date.now(),
      });
    } catch (error) {
      const parsed = getApiErrorMessage(
        error,
        "Unable to resolve call. Please try again."
      );
      setSubmitError(parsed.message);
    } finally {
      setSubmitting(false);
    }
  }, [category, followUpRequired, notes, onSubmit]);

  const displayedSubmitError = externalSubmitError ?? submitError;

  if (!isOpen) return null;

  return (
    <div className={cn("fixed inset-0 z-60", className)}>
      <button
        type="button"
        aria-label="Close resolve call modal"
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />

      <div className="relative flex min-h-full items-center justify-center p-6">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Resolve a Call"
          className={cn(
            "w-235 max-w-[calc(100vw-24px)]",
            "rounded-[18px] border border-[#2C313B]",
            "bg-[#0B111C] text-white",
            "shadow-[0_26px_90px_rgba(0,0,0,0.65)]"
          )}
        >
          <div className="px-10 py-8 md:px-12 md:py-10">
            <h2 className="text-[20px] font-semibold leading-7 text-white">
              Resolve a Call
            </h2>

            <div className="mt-10">
              <DropdownField
                label="Call Category"
                options={[...RESOLVE_CALL_OPTIONS]}
                value={category}
                onChange={(value) => {
                  setCategory(value);
                  if (categoryError) setCategoryError(null);
                  if (
                    notesError &&
                    value &&
                    !REQUIRED_NOTES_CATEGORIES.has(value)
                  ) {
                    setNotesError(null);
                  }
                }}
                placeholder="Please Select"
                error={categoryError ?? undefined}
                fieldClassName="h-12 rounded-[14px] px-6"
                menuClassName="mt-2 rounded-[14px] bg-[#172334]"
              />
            </div>

            <div className="mt-8">
              <label className="block text-[14px] font-normal leading-5 text-[#99A1AA]">
                Resolution Notes
                {notesRequired ? (
                  <span className="ml-2 text-[12px] text-red-300">
                    Required for this category
                  </span>
                ) : null}
              </label>
              <textarea
                value={notes}
                onChange={(event) => {
                  setNotes(event.target.value);
                  if (notesError) setNotesError(null);
                  if (submitError) setSubmitError(null);
                }}
                spellCheck={true}
                placeholder="Please elaborate"
                className={cn(
                  "mt-3 h-64 w-full resize-none rounded-2xl border",
                  notesError ? "border-red-500" : "border-[#2C313B]",
                  "bg-transparent px-6 py-5 text-[16px] leading-6 text-[#99A1AA]",
                  "placeholder:text-[#727C92]",
                  "focus:outline-none",
                  notesError ? "focus:border-red-500" : "focus:border-[#2C313B]"
                )}
              />
              {notesError ? (
                <div className="mt-2 text-[12px] text-red-300">
                  {notesError}
                </div>
              ) : null}
            </div>

            <div className="mt-6">
              <label className="inline-flex cursor-pointer items-center gap-3 text-[14px] text-white">
                <input
                  type="checkbox"
                  checked={followUpRequired}
                  onChange={(event) =>
                    setFollowUpRequired(event.target.checked)
                  }
                  className="h-4 w-4 rounded border-[#2C313B] bg-transparent accent-[#1C75BC]"
                />
                Follow-up Required
              </label>
            </div>

            {displayedSubmitError ? (
              <div className="mt-3 text-[12px] text-red-300">{displayedSubmitError}</div>
            ) : null}

            <div className="mt-10 flex flex-wrap items-center justify-end gap-4">
              <SecondaryButton
                className="h-11 min-w-35 px-8 text-[16px] text-white"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </SecondaryButton>
              {onDeferAndNavigateDashboard ? (
                <SecondaryButton
                  data-testid="resolve-call-return-dashboard"
                  className="h-11 min-w-45 px-8 text-[16px] text-white"
                  onClick={onDeferAndNavigateDashboard}
                  disabled={submitting}
                >
                  Return to Dashboard
                </SecondaryButton>
              ) : null}
              <PrimaryButton
                className="h-11 min-w-45 px-8 text-[16px]"
                onClick={() => {
                  void handleSubmit();
                }}
                loading={submitting}
                disabled={submitting}
              >
                Confirm
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
